<script setup>
import EnWidget from 'Components/widgets/EnWidget/EnWidget.vue';
import EnIcon from 'Components/icons/EnIcon.vue';
import { computed } from 'vue';

const props = defineProps({
  /**
   * Extra classes to append to the wrapper
   */
  widgetClass: {
    type: String,
    default: ''
  },

  /**
   * Optional icon class (e.g. 'icon-enloc_logo_min')
   */
  iconClass: {
    type: String,
    default: ''
  },

  /**
   * Optional title/subtitle shown at the top
   */
  title: {
    type: String,
    default: ''
  },
  subtitle: {
    type: String,
    default: ''
  },

  /**
   * Max width of the card (px)
   */
  maxWidth: {
    type: [Number, String],
    default: 450
  },

  /**
   * Inner padding (px)
   */
  padding: {
    type: [Number, String],
    default: 32
  },

  /**
   * Center vertically & horizontally like the auth pages
   */
  centered: {
    type: Boolean,
    default: true
  },
});

const cardStyle = computed(() => {
  const pixels = (value) => (typeof value === 'number' ? `${value}px` : String(value));
  const maxWidth = pixels(props.maxWidth);
  return {
    width: maxWidth,
    padding: pixels(props.padding),
  };
});
</script>

<template>
  <div :class="['en-widget-card-page', { 'is-centered': centered }]">
    <en-widget
        :widget-class="['en-widget-wrapper', widgetClass]"
        :style="cardStyle"
    >
      <div v-if="iconClass" class="en-icon-container">
        <en-icon size="30" class="en-icon icon-32" :class="iconClass"/>
      </div>

      <div v-if="title || subtitle" class="page-title-with-icon">
        <h5 class="en-title" v-if="title">{{ title }}</h5>
        <div class="en-subtitle" v-if="subtitle">{{ subtitle }}</div>
      </div>

      <div class="en-widget-content">
        <slot></slot>
      </div>

      <div class="en-widget-actions">
        <slot name="actions"></slot>
      </div>
    </en-widget>
  </div>
</template>

<style scoped lang="scss">
@use "Styles/helper/variables.scss";

.en-widget-card-page {
  &.is-centered {
    min-height: 80vh;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.en-widget-wrapper {
  width: v-bind(maxWidth + 'px');
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  padding: v-bind(padding + 'px');
  min-height: 100%;
}

.en-icon-container {
  border: 1px solid variables.$login-page-icon-container-border;
  border-radius: 12px;
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, .1);
}

.en-icon {
  color: variables.$en-red !important;
}

.en-title {
  font-size: 1.5rem;
  font-weight: 500;
  margin-top: 10px;
  color: variables.$en-blue !important;
}

.en-subtitle {
  opacity: .8;
}

.en-widget-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 auto;
  text-align: left;
}

.en-widget-actions {
  width: calc(100% + 40px);
  display: flex;
  justify-content: center;
  margin-top: auto;
}
</style>
